import type {
  DepartmentHealthEffortLevel,
  DepartmentHealthProfileDecisionGuide,
  DepartmentHealthProfileSummary,
} from '../../types/DepartmentHealthTypes';

export interface DepartmentHealthProfileDecisionGuideContext {
  belowThresholdProfiles: DepartmentHealthProfileSummary[];
  developPathCount: number;
  redeployPathCount: number;
  mostUrgentProfile: DepartmentHealthProfileSummary | null;
}

const sortByScoreAscending = (
  profiles: DepartmentHealthProfileSummary[],
): DepartmentHealthProfileSummary[] =>
  [...profiles].sort((left, right) => {
    const leftScore = left.targetProfileAlignmentScore ?? Number.POSITIVE_INFINITY;
    const rightScore = right.targetProfileAlignmentScore ?? Number.POSITIVE_INFINITY;

    return leftScore - rightScore;
  });

export const getBelowThresholdProfiles = (
  guide: DepartmentHealthProfileDecisionGuide,
): DepartmentHealthProfileSummary[] => {
  const byId = new Map<string, DepartmentHealthProfileSummary>();

  [...guide.redeployCandidates, ...guide.mostUrgent].forEach((profile) => {
    if (profile.profileId) {
      byId.set(profile.profileId, profile);
    }
  });

  return sortByScoreAscending([...byId.values()]);
};

export const buildDepartmentHealthProfileDecisionGuideContext = (
  guide: DepartmentHealthProfileDecisionGuide,
): DepartmentHealthProfileDecisionGuideContext => {
  const belowThresholdProfiles = getBelowThresholdProfiles(guide);

  return {
    belowThresholdProfiles,
    developPathCount: guide.mostUrgent.length,
    redeployPathCount: guide.redeployCandidates.length,
    mostUrgentProfile: belowThresholdProfiles[0] ?? null,
  };
};

export const isRedeploymentPath = (profile: DepartmentHealthProfileSummary): boolean => profile.path === 'redeploy';

export const getEffortLevelFromGapCount = (gapCount: number): DepartmentHealthEffortLevel => {
  if (gapCount <= 3) {
    return 'low';
  }
  if (gapCount <= 7) {
    return 'moderate';
  }

  return 'high';
};
